import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const CompactTemplateSelector = ({
  templates,
  expandedTemplates,
  setExpandedTemplates,
  onDeleteTemplate,
  onCopyTemplate,
  openAnrModalForTemplate,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleTemplate = (templateId) => {
    setExpandedTemplates((prev) => ({
      ...prev,
      [templateId]: !prev[templateId],
    }));
  };

  const getTotalProcesses = (template) => {
    return template.processes?.length || 0;
  };

  const getScheduledProcesses = (template) => {
    // This would need to check calendarData - pass as prop if needed
    return 0; // Placeholder
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title="Select Template">
        <Button
          variant="outlined"
          onClick={handleClick}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
            },
          }}
        >
          <AssignmentIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>
            Vorlagen
          </Typography>
          <Badge badgeContent={templates.length} color="primary" sx={{ ml: 1.5 }} />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: '70vh',
            width: '400px',
            mt: 1,
            borderRadius: 2,
            boxShadow: 3,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Projektvorlagen
            </Typography>
            <Tooltip title="Neue Vorlage">
              <IconButton size="small" color="primary">
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {templates.length} {templates.length === 1 ? 'Vorlage' : 'Vorlagen'} verfügbar
          </Typography>
        </Box>

        <Divider />

        {templates.map((template, index) => (
          <React.Fragment key={template._id}>
            <MenuItem
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                py: 1.5,
                px: 2,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {/* Template Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTemplate(template._id);
                    }}
                  >
                    {expandedTemplates[template._id] ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Typography variant="body1" fontWeight={600}>
                    {template.anr || `Template ${index + 1}`}
                  </Typography>
                  <Chip
                    label={`${getTotalProcesses(template)} Prozesse`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="ANR zuweisen">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAnrModalForTemplate(template._id);
                        handleClose();
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kopieren">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyTemplate(template);
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTemplate(template._id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Template Description */}
              <Typography variant="body2" color="text.secondary" sx={{ pl: 5 }}>
                {template.auftragsbezeichnung || 'Keine Beschreibung'}
              </Typography>

              {/* Expanded Processes List */}
              {expandedTemplates[template._id] && (
                <Box
                  sx={{
                    mt: 1,
                    pl: 5,
                    pr: 1,
                    maxHeight: 200,
                    overflowY: 'auto',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {template.processes && template.processes.length > 0 ? (
                    template.processes.map((process, pIndex) => (
                      <Box
                        key={process.id || pIndex}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: 0.5,
                          borderBottom:
                            pIndex < template.processes.length - 1
                              ? '1px solid'
                              : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.primary">
                          {process.label || `Prozess ${pIndex + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {process.hours || 0}h
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      Keine Prozesse
                    </Typography>
                  )}
                </Box>
              )}
            </MenuItem>
            {index < templates.length - 1 && <Divider />}
          </React.Fragment>
        ))}

        {templates.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Keine Vorlagen verfügbar
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
            >
              Vorlage erstellen
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default CompactTemplateSelector;
